// Client-safe analytics helper. No "server-only" import here — this runs in the
// browser. Types are redefined locally (not imported from the server-only data
// module) so the client bundle never pulls server code.

export type TrackingEventKey =
  | "page_view"
  | "view_item"
  | "view_item_list"
  | "select_item"
  | "add_to_cart"
  | "remove_from_cart"
  | "view_cart"
  | "begin_checkout"
  | "add_shipping_info"
  | "add_payment_info"
  | "purchase"
  | "search"

export type TrackingSettings = {
  enabled: boolean
  gtmEnabled: boolean
  gtmId: string
  firstPartyEndpointEnabled: boolean
  firstPartyEndpointShadowMode: boolean
  defaultCurrency: string
  defaultBrand: string
  affiliation: string
  eventNames: Record<TrackingEventKey, string>
  enabledEvents: Record<TrackingEventKey, boolean>
  meta: { contentType: "product_group" | "product" }
  privacy: {
    normalizePhoneToE164: boolean
    includeUserDataOnPurchase: boolean
    includeUserDataOnBeginCheckout: boolean
  }
  debug: { logToConsole: boolean }
}

const DEFAULT_NAMES: Record<TrackingEventKey, string> = {
  page_view: "page_view",
  view_item: "view_item",
  view_item_list: "view_item_list",
  select_item: "select_item",
  add_to_cart: "add_to_cart",
  remove_from_cart: "remove_from_cart",
  view_cart: "view_cart",
  begin_checkout: "begin_checkout",
  add_shipping_info: "add_shipping_info",
  add_payment_info: "add_payment_info",
  purchase: "purchase",
  search: "search",
}

// ── Module-level settings mirror (V2.1-2) ───────────────────────────────────────
// Set synchronously by the TrackingProvider during render (not in useEffect), so
// even direct pushEvent() calls resolve the correct configured names/flags before
// any child tracking effect fires.

let SETTINGS: TrackingSettings | null = null
let KNOWN_IDENTITY: KnownTrackingIdentity | null = null
let LAST_ENGAGEMENT_EVENT_MS = 0

export function setTrackingSettings(s: TrackingSettings) {
  SETTINGS = s
}

export function getTrackingSettings(): TrackingSettings | null {
  return SETTINGS
}

export type KnownTrackingIdentity = {
  email?: string | null
  phone?: string | null
  first_name?: string | null
  last_name?: string | null
} | null

export function setKnownTrackingIdentity(identity: KnownTrackingIdentity) {
  KNOWN_IDENTITY = identity
}

export function getKnownTrackingIdentity(): KnownTrackingIdentity {
  return KNOWN_IDENTITY
}

// ── Identity readiness gate (owner requirement, 2026-07-02) ─────────────────
// The login identity is fetched client-side (GET /api/identity) after mount, so
// a logged-in shopper's FIRST view_item could otherwise race that fetch and
// reach Meta ViewContent without em/ph. Rule: view_item forwarding WAITS until
// the identity fetch has SETTLED — logged-in → user_data attached; guest or
// fetch failure → the event proceeds without it. There is deliberately NO
// timeout racing the gate (a logged-in ViewContent must never be sent
// identity-less just because a timer won); the fetch itself may abort/fail,
// which settles the gate as guest. page_view / add_to_cart / begin_checkout do
// not wait — their Meta CAPI tags read only fbp/fbc/external_id (verified in
// GTM-P566HM7Z, 2026-07-02); purchase carries order-derived user_data in its
// own payload.
let IDENTITY_SETTLED: Promise<void> = Promise.resolve()
let settleIdentityGate: (() => void) | null = null

/**
 * Arm the gate. Must run before any child tracking effect can fire. Safe to call
 * again on a soft navigation (M5 mid-session login refresh): any still-pending
 * resolver is settled first so an earlier identityReady() awaiter can never be
 * stranded on an orphaned promise.
 */
export function beginIdentityResolution() {
  settleIdentityGate?.()
  IDENTITY_SETTLED = new Promise((resolve) => {
    settleIdentityGate = resolve
  })
}

/** Settle the gate with the fetched identity (null = guest / fetch failed). */
export function completeIdentityResolution(identity: KnownTrackingIdentity) {
  KNOWN_IDENTITY = identity
  settleIdentityGate?.()
  settleIdentityGate = null
}

/** Resolves when the identity fetch has settled (immediately if never armed). */
export function identityReady(): Promise<void> {
  return IDENTITY_SETTLED
}

export function buildTrackingEngagement(
  sessionId: string,
  nowMs = Date.now()
) {
  const hasPerformance =
    typeof performance !== "undefined" && typeof performance.now === "function"
  const firstEventElapsed = hasPerformance ? Math.round(performance.now()) : 1
  const elapsed = LAST_ENGAGEMENT_EVENT_MS
    ? nowMs - LAST_ENGAGEMENT_EVENT_MS
    : firstEventElapsed
  LAST_ENGAGEMENT_EVENT_MS = nowMs

  let sessionNumber = 1
  try {
    const storage =
      typeof window !== "undefined" ? window.localStorage : undefined
    const previousSessionId = storage?.getItem("alif_last_sid")
    const storedSessionNumber = Number(storage?.getItem("alif_sno") || "0")

    if (previousSessionId === sessionId && storedSessionNumber > 0) {
      sessionNumber = storedSessionNumber
    } else {
      sessionNumber = Math.max(1, storedSessionNumber + 1)
      storage?.setItem("alif_last_sid", sessionId)
      storage?.setItem("alif_sno", String(sessionNumber))
    }
  } catch {
    sessionNumber = 1
  }

  return {
    // Clamp to the schema hard-max (event-schema.ts trackingEngagementSchema
    // .max(60*60*1000)): a tab backgrounded >1h then refocused would otherwise
    // produce a value zod rejects, and /api/events would 400 the event away.
    engagement_time_msec: Math.min(3_600_000, Math.max(1, Math.round(elapsed))),
    session_number: sessionNumber,
  }
}

export function resetTrackingEngagementForTests() {
  LAST_ENGAGEMENT_EVENT_MS = 0
}

// ── dataLayer types ─────────────────────────────────────────────────────────────

export type GtmItem = {
  item_id: string
  item_name: string
  item_brand?: string
  item_category?: string
  item_category2?: string
  item_variant?: string
  price?: number
  quantity?: number
  discount?: number
  index?: number
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
  }
}

export function generateEventId(key: string, seed?: string): string {
  const rand = Math.random().toString(36).slice(2, 8)
  return `${key}_${seed ? seed + "_" : ""}${Date.now()}_${rand}`
}

export function normalizePageLocationForTracking(url: string): string {
  if (!url) return url

  try {
    const parsed = new URL(url)
    // GTM receives the storefront's actual_url DLV; live hits show the internal
    // selected-variant param is absent while attribution params remain intact.
    parsed.searchParams.delete("v_id")
    return parsed.toString()
  } catch {
    return url
  }
}

/**
 * Core push. Resolves the outgoing event name from Admin settings, respects the
 * master + per-event enable flags, clears the previous ecommerce object, and
 * pushes. Safe on the server (no-op).
 */
export function pushEvent(
  internalKey: TrackingEventKey,
  payload: Record<string, unknown>
): void {
  if (typeof window === "undefined") return

  const settings = SETTINGS
  if (!settings || !settings.enabled) return
  if (!settings.enabledEvents[internalKey]) return

  const eventName = settings.eventNames[internalKey] || DEFAULT_NAMES[internalKey]

  window.dataLayer = window.dataLayer || []
  // Clear stale ecommerce so GTM never reuses a previous event's items.
  window.dataLayer.push({ ecommerce: null })
  window.dataLayer.push({ event: eventName, ...payload })

  if (settings.firstPartyEndpointEnabled) {
    void sendFirstPartyEvent(internalKey, payload)
  }

  if (settings.debug?.logToConsole) {
    // eslint-disable-next-line no-console
    console.log("[track]", eventName, payload)
  }
}

async function sendFirstPartyEvent(
  internalKey: TrackingEventKey,
  payload: Record<string, unknown>
) {
  if (!isLiveEndpointEvent(internalKey)) return

  try {
    // Meta ViewContent consumes em/ph — wait for the login-identity fetch to
    // settle so a logged-in shopper's view_item is never forwarded without
    // user_data (see identityReady gate above).
    if (internalKey === "view_item") {
      await identityReady()
    }
    const [
      { sendTrackingEvent },
      { ensureBrowserIdentity, getBrowserTrackingConsent },
    ] = await Promise.all([import("./first-party-client"), import("./identity")])

    const ecommerce = payload.ecommerce as
      | {
          currency?: string
          value?: number
          transaction_id?: string
          affiliation?: string
          coupon?: string
          tax?: number
          shipping?: number
          payment_type?: string
          shipping_tier?: string
          total_item_quantity?: number
          item_list_id?: string
          item_list_name?: string
          items?: GtmItem[]
        }
      | undefined

    const consent = getBrowserTrackingConsent()
    const ids = ensureBrowserIdentity(consent)
    const knownUserData =
      consent.ad_user_data === "granted" && KNOWN_IDENTITY
        ? buildUserData({
            email: KNOWN_IDENTITY.email,
            address: {
              first_name: KNOWN_IDENTITY.first_name,
              last_name: KNOWN_IDENTITY.last_name,
              phone: KNOWN_IDENTITY.phone,
            },
          })
        : undefined
    const payloadUserData =
      consent.ad_user_data === "granted" && payload.user_data
        ? (payload.user_data as Record<string, unknown>)
        : undefined
    const userData =
      knownUserData || payloadUserData
        ? { ...(knownUserData || {}), ...(payloadUserData || {}) }
        : undefined
    const hasWin = typeof window !== "undefined"
    const url =
      (payload.actual_url as string | undefined) ||
      (hasWin ? window.location.href : "")
    const pageUrl = normalizePageLocationForTracking(url)
    const path =
      (payload.page_path as string | undefined) ||
      (hasWin ? window.location.pathname : undefined)

    await sendTrackingEvent(
      {
        schema_version: "1.0",
        event_name: internalKey,
        event_id: String(payload.event_id || generateEventId(internalKey)),
        occurred_at: new Date().toISOString(),
        page: {
          url: pageUrl,
          path,
          title:
            (payload.page_title as string | undefined) ||
            (hasWin ? document.title : undefined),
          referrer:
            (payload.page_referrer as string | undefined) ||
            (hasWin ? document.referrer : undefined),
        },
        consent,
        ids,
        engagement: buildTrackingEngagement(ids.session_id),
        ...(ecommerce ? { ecommerce } : {}),
        ...(userData ? { user_data: userData as Record<string, string> } : {}),
        ...(payload.search_term
          ? { search_term: String(payload.search_term) }
          : {}),
      },
      { keepalive: internalKey === "purchase" }
    )
  } catch {
    // Tracking must never throw into the shopping experience.
  }
}

function isLiveEndpointEvent(
  internalKey: TrackingEventKey
): internalKey is
  | "page_view"
  | "view_item"
  | "view_item_list"
  | "select_item"
  | "add_to_cart"
  | "remove_from_cart"
  | "view_cart"
  | "begin_checkout"
  | "add_shipping_info"
  | "add_payment_info"
  | "purchase"
  | "search" {
  return (
    internalKey === "page_view" ||
    internalKey === "view_item" ||
    internalKey === "view_item_list" ||
    internalKey === "select_item" ||
    internalKey === "add_to_cart" ||
    internalKey === "remove_from_cart" ||
    internalKey === "view_cart" ||
    internalKey === "begin_checkout" ||
    internalKey === "add_shipping_info" ||
    internalKey === "add_payment_info" ||
    internalKey === "purchase" ||
    internalKey === "search"
  )
}

// ── Normalization helpers (Meta match quality) ──────────────────────────────────

function normEmail(email?: string | null): string | undefined {
  if (!email) return undefined
  return email.trim().toLowerCase() || undefined
}

function normPhone(phone?: string | null): string | undefined {
  if (!phone) return undefined
  if (!SETTINGS?.privacy?.normalizePhoneToE164) return phone.trim() || undefined
  const digits = phone.replace(/[^\d]/g, "")
  if (!digits) return undefined
  if (digits.startsWith("880")) {
    // Collapse a duplicated trunk zero ("+880 01712…" → 8800…) back to a valid
    // 13-digit BD E.164; country code 880 is Bangladesh-only, so a following 0 is
    // always the leftover trunk 0. Backend hashPhone (meta-capi.ts) applies the
    // identical rule so both paths normalize to the same number.
    return `+${digits.replace(/^8800/, "880")}`
  }
  if (digits.startsWith("0")) return `+88${digits}`
  if (digits.startsWith("1") && digits.length === 10) return `+880${digits}`
  return `+${digits}`
}

export type AddressLike = {
  first_name?: string | null
  last_name?: string | null
  city?: string | null
  province?: string | null
  postal_code?: string | null
  phone?: string | null
} | null

/**
 * Emit ALL field aliases the current + future GTM variables may read (see plan
 * finding 16): web container reads user_data.zip and has no `state`, but we send
 * both zip + postal_code + state so a later GTM standardization needs no code
 * change.
 */
export function buildUserData(opts: {
  email?: string | null
  address?: AddressLike
}): Record<string, unknown> {
  const a = opts.address || undefined
  const out: Record<string, unknown> = {}

  const email = normEmail(opts.email)
  const phone = normPhone(a?.phone)

  if (email) out.email = email
  if (phone) out.phone = phone
  if (a?.first_name) out.first_name = a.first_name
  if (a?.last_name) out.last_name = a.last_name
  if (a?.city) out.city = a.city
  if (a?.province) out.state = a.province
  if (a?.postal_code) {
    out.zip = a.postal_code
    out.postal_code = a.postal_code
  }
  out.country = "bd"

  return out
}

// ── Item builders ────────────────────────────────────────────────────────────────

type AnyProduct = {
  id?: string | null
  title?: string | null
  metadata?: Record<string, unknown> | null
  categories?: { name?: string | null }[] | null
}
type AnyVariant = {
  id?: string | null
  sku?: string | null
  options?: { value?: string | null }[] | null
  calculated_price?: {
    calculated_amount?: number | null
    original_amount?: number | null
  } | null
}

export function buildItemFromProduct(
  product: AnyProduct,
  variant: AnyVariant | undefined | null,
  quantity = 1
): GtmItem {
  const brand = SETTINGS?.defaultBrand || process.env.NEXT_PUBLIC_BRAND_NAME || "Store"
  const category = product.categories?.[0]?.name || "Fashion"
  const category2 = product.categories?.[1]?.name || undefined
  const variantLabel =
    variant?.options
      ?.map((o) => o?.value)
      .filter(Boolean)
      .join(" / ") || undefined

  // BDT is stored as a whole-number amount — do NOT divide by 100 (plan §18).
  const price = variant?.calculated_price?.calculated_amount ?? undefined
  const original = variant?.calculated_price?.original_amount ?? undefined
  const discount =
    typeof original === "number" && typeof price === "number" && original > price
      ? original - price
      : undefined

  // Clean customer-facing name (metadata.display_name → title fallback, same
  // rule as lib/util/get-display-title.ts) so GA4/Meta reports match the
  // storefront H1 and the Facebook feed <title>. item_id (SKU) is the catalog
  // matcher and stays untouched.
  const displayName = product.metadata?.["display_name"]
  return {
    item_id: variant?.sku || variant?.id || product.id || "",
    item_name:
      (typeof displayName === "string" && displayName.trim()
        ? displayName.trim()
        : "") ||
      product.title ||
      "",
    item_brand: brand,
    item_category: category,
    ...(category2 ? { item_category2: category2 } : {}),
    ...(variantLabel ? { item_variant: variantLabel } : {}),
    ...(typeof price === "number" ? { price } : {}),
    quantity,
    ...(discount ? { discount } : {}),
  }
}

type AnyLineItem = {
  variant_id?: string | null
  variant_sku?: string | null
  product_id?: string | null
  product_title?: string | null
  title?: string | null
  variant_title?: string | null
  quantity?: number | null
  unit_price?: number | null
  // Cart fetches expand *items.product, so the clean display name is usually
  // available; fall back to the raw product_title when it isn't.
  product?: { metadata?: Record<string, unknown> | null } | null
}

export function buildItemFromLineItem(item: AnyLineItem): GtmItem {
  const brand = SETTINGS?.defaultBrand || process.env.NEXT_PUBLIC_BRAND_NAME || "Store"
  const displayName = item.product?.metadata?.["display_name"]
  return {
    item_id: item.variant_sku || item.variant_id || item.product_id || "",
    item_name:
      (typeof displayName === "string" && displayName.trim()
        ? displayName.trim()
        : "") ||
      item.product_title ||
      item.title ||
      "",
    item_brand: brand,
    ...(item.variant_title ? { item_variant: item.variant_title } : {}),
    ...(typeof item.unit_price === "number" ? { price: item.unit_price } : {}),
    quantity: item.quantity ?? 1,
  }
}

export function sumQuantity(items: { quantity?: number }[]): number {
  return items.reduce((sum, i) => sum + (i.quantity ?? 0), 0)
}

export function currency(): string {
  return SETTINGS?.defaultCurrency || "BDT"
}

export function affiliation(): string {
  return (
    SETTINGS?.affiliation ||
    process.env.NEXT_PUBLIC_TRACKING_AFFILIATION ||
    `${process.env.NEXT_PUBLIC_BRAND_SHORT_NAME || process.env.NEXT_PUBLIC_BRAND_NAME || "Store"} Online Store`
  )
}
